import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface FlaggedPlayer {
  id: number;
  name: string;
  email: string;
  age?: number;
  hs_grad_year?: number;
  university_grad_year?: number;
  current_club?: string;
  specific_positions?: string[];
  hs_grad_year_in_email?: boolean;
  email_content?: string;
  position?: string[];
  nationality?: string;
  height?: string;
  weight?: string;
  playing_level?: string;
  gpa?: any;
  highlight_videos?: string[];
  distinguishments?: string[];
  international_caps?: any[];
}

interface EmailContentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: FlaggedPlayer;
  onPlayerUpdate?: (updatedPlayer: Partial<FlaggedPlayer>) => void;
}

export default function EmailContentViewModal({
  isOpen,
  onClose,
  player,
  onPlayerUpdate,
}: EmailContentViewModalProps) {
  const { toast } = useToast();
  
  // State for editing graduation year
  const [isEditing, setIsEditing] = useState(false);
  const [editedGradYear, setEditedGradYear] = useState<string>(
    player.hs_grad_year?.toString() || ""
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingGradYear, setPendingGradYear] = useState<number | null>(null);

  // Reset editing state when modal opens or player changes
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setEditedGradYear(player.hs_grad_year?.toString() || "");
      setShowConfirmDialog(false);
      setPendingGradYear(null);
    }
  }, [isOpen, player.id, player.hs_grad_year]);

  // Update graduation year mutation
  const updateGradYearMutation = useMutation({
    mutationFn: async (gradYear: number) => {
      console.log('Updating graduation year for player:', player.id, 'to:', gradYear);
      const response = await apiRequest("PUT", `/api/email-sending/update-graduation-year/${player.id}`, {
        hs_grad_year: gradYear
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to update graduation year: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      console.log('Graduation year update successful:', data);
      
      const oldYear = player.hs_grad_year;
      const newYear = data.updated_player.hs_grad_year;
      const wasSmallChange = oldYear && Math.abs(newYear - oldYear) <= 2;
      
      toast({
        title: "✅ Graduation year updated!",
        description: wasSmallChange 
          ? `Updated ${player.name}'s graduation year to ${newYear}` 
          : `Updated ${player.name}'s graduation year from ${oldYear || 'N/A'} to ${newYear} (manually corrected)`,
      });
      
      // Update parent component with the new data
      if (onPlayerUpdate) {
        onPlayerUpdate({
          id: player.id,
          hs_grad_year: data.updated_player.hs_grad_year,
          hs_grad_year_in_email: data.updated_player.hs_grad_year_in_email,
        });
      }
      
      // Reset editing state
      setIsEditing(false);
      setShowConfirmDialog(false);
      setPendingGradYear(null);
      
      // Update local state to reflect the change immediately
      setEditedGradYear(data.updated_player.hs_grad_year.toString());
    },
    onError: (error: any) => {
      console.error('Graduation year update failed:', error);
      
      const errorMessage = error.message || "Something went wrong. Please try again.";
      
      toast({
        title: "❌ Failed to update graduation year",
        description: errorMessage,
        variant: "destructive",
      });
      
      // CRITICAL: Reset ALL states to prevent UI freeze
      setShowConfirmDialog(false);
      setPendingGradYear(null);
      setIsEditing(false);
      
      // Reset the edited value to original
      setEditedGradYear(player.hs_grad_year?.toString() || "");
      
      console.log('All states reset after error to prevent UI freeze');
    },
  });

  const handleSaveGradYear = () => {
    // Prevent action if already updating
    if (updateGradYearMutation.isPending) {
      return;
    }
    
    const gradYear = parseInt(editedGradYear.trim());
    
    // Validate graduation year
    const currentYear = new Date().getFullYear();
    if (!editedGradYear.trim()) {
      toast({
        title: "Graduation year required",
        description: "Please enter a graduation year (e.g., 2025)",
        variant: "destructive",
      });
      return;
    }
    
    if (isNaN(gradYear)) {
      toast({
        title: "Invalid graduation year",
        description: "Please enter a valid 4-digit year (e.g., 2025)",
        variant: "destructive",
      });
      return;
    }
    
    if (gradYear < (currentYear - 10) || gradYear > (currentYear + 10)) {
      toast({
        title: "Graduation year out of range",
        description: `Please enter a year between ${currentYear - 10} and ${currentYear + 10}. If this is correct, contact support.`,
        variant: "destructive",
      });
      return;
    }
    
    // Check if the value actually changed
    if (gradYear === player.hs_grad_year) {
      setIsEditing(false);
      return;
    }
    
    // Handle confirmation logic based on current state
    const playerCurrentGradYear = player.hs_grad_year;
    const isNewValue = !playerCurrentGradYear; // No existing graduation year
    
    console.log('Graduation year update logic:', {
      currentValue: playerCurrentGradYear,
      newValue: gradYear,
      isNewValue,
      playerName: player.name
    });
    
    if (isNewValue) {
      // No existing graduation year - save directly without confirmation for reasonable values
      if (gradYear >= (currentYear - 5) && gradYear <= (currentYear + 10)) {
        console.log('New graduation year within reasonable range, saving directly:', gradYear);
        updateGradYearMutation.mutate(gradYear);
      } else {
        // Unusual year for new value - show confirmation
        console.log('New graduation year outside typical range, showing confirmation:', gradYear);
        setPendingGradYear(gradYear);
        setShowConfirmDialog(true);
      }
    } else {
      // Existing graduation year - check difference
      const yearDifference = Math.abs(gradYear - playerCurrentGradYear);
      
      if (yearDifference <= 2) {
        // Small change - update directly
        console.log('Small graduation year change, updating directly:', gradYear);
        updateGradYearMutation.mutate(gradYear);
      } else {
        // Large change - show confirmation
        console.log('Large graduation year change, showing confirmation:', gradYear);
        setPendingGradYear(gradYear);
        setShowConfirmDialog(true);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveGradYear();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleConfirmUpdate = () => {
    console.log('handleConfirmUpdate called', {
      pendingGradYear,
      isPending: updateGradYearMutation.isPending,
      showConfirmDialog
    });
    
    if (pendingGradYear && !updateGradYearMutation.isPending) {
      console.log('Confirming graduation year update:', pendingGradYear);
      updateGradYearMutation.mutate(pendingGradYear);
    } else {
      console.warn('Cannot confirm update:', {
        noPendingYear: !pendingGradYear,
        alreadyPending: updateGradYearMutation.isPending
      });
      
      // Safety reset if something is wrong
      if (!pendingGradYear) {
        setShowConfirmDialog(false);
        console.log('Reset confirmation dialog due to missing pending year');
      }
    }
  };

  const handleCancelEdit = () => {
    // Prevent cancel if currently updating
    if (updateGradYearMutation.isPending) {
      return;
    }
    
    setIsEditing(false);
    setEditedGradYear(player.hs_grad_year?.toString() || "");
    setShowConfirmDialog(false);
    setPendingGradYear(null);
  };
  
  // Get email content - backend should decrypt this field properly
  const getEmailContent = (player: FlaggedPlayer): string => {
    const content = player.email_content || "";
    
    // Debug log for troubleshooting if content is unexpectedly empty
    if (!content) {
      console.warn('EmailContentViewModal: No email content available', {
        playerId: player.id,
        playerName: player.name,
        hasEmailContent: !!player.email_content,
        emailContentLength: player.email_content?.length || 0
      });
    }
    
    return content;
  };

  const renderEmailContentWithHighlight = (player: FlaggedPlayer, hsGradYear: number | undefined) => {
    const content = getEmailContent(player);
    
    if (!content) {
      return "No email content available";
    }
    
    if (!hsGradYear) {
      return content;
    }
    
    // Highlight the graduation year in the email content
    const yearStr = hsGradYear.toString();
    
    // Create highlighted version using dangerouslySetInnerHTML
    const highlighted = content.replace(
      new RegExp(`\\b${yearStr}\\b`, 'gi'),
      `<mark style="background-color: #fef3c7; padding: 2px 4px; border-radius: 2px; font-weight: bold; color: #92400e;">${yearStr}</mark>`
    );
    
    return highlighted;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col" style={{ zIndex: 70 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg">
              <AvatarPlaceholder
                name={player.name}
                className="h-8 w-8 mr-3"
              />
              <span>{player.name} - Email Content Review</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Player Summary */}
            <div className="bg-neutral-50 p-4 rounded-lg border">
              <h3 className="text-sm font-medium text-neutral-800 mb-3">Player Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-neutral-600">Age:</span>
                  <div className="text-neutral-900">{player.age || 'N/A'}</div>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">HS Grad Year:</span>
                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <div className="flex items-center space-x-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <Input
                          type="number"
                          value={editedGradYear}
                          onChange={(e) => setEditedGradYear(e.target.value)}
                          className="w-24 h-8 text-sm font-medium border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="2025"
                          min={new Date().getFullYear() - 10}
                          max={new Date().getFullYear() + 10}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          title="Press Enter to save, Escape to cancel"
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveGradYear}
                          disabled={updateGradYearMutation.isPending}
                          className="h-8 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                          title="Save changes (Enter)"
                        >
                          {updateGradYearMutation.isPending ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-check"></i>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          className="h-8 px-2 text-xs hover:bg-red-100 text-red-600 hover:text-red-700"
                          title="Cancel changes (Escape)"
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                        <div className="text-xs text-blue-600 ml-2">
                          <i className="fas fa-keyboard mr-1"></i>
                          Enter saves, Esc cancels
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <div className="text-neutral-900 font-bold">
                          {player.hs_grad_year || 'N/A'}
                          {player.hs_grad_year_in_email === false && (
                            <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                              Inferred
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setIsEditing(true)}
                          className="h-8 px-2 text-xs text-blue-600 hover:text-blue-800"
                          title="Edit graduation year"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">School:</span>
                  <div className="text-neutral-900">{player.current_club || 'N/A'}</div>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Position:</span>
                  <div className="text-neutral-900">
                    {player.specific_positions?.join(', ') || 
                     player.position?.join(', ') || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Warning Alert */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="fas fa-exclamation-triangle text-amber-400"></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    Graduation Year Highlighted Below
                  </h3>
                  <div className="mt-1 text-sm text-amber-700">
                    {player.hs_grad_year_in_email === false ? (
                      <p>
                        The graduation year <strong>{player.hs_grad_year}</strong> was inferred by our AI model 
                        and highlighted in yellow below. Please verify this information is accurate.
                      </p>
                    ) : (
                      <p>
                        No graduation year information was found in the original email content.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Original Email Content */}
            <div className="bg-white border border-neutral-200 rounded-lg flex flex-col">
              <div className="bg-neutral-50 px-4 py-3 border-b border-neutral-200 flex-shrink-0">
                <h3 className="text-sm font-medium text-neutral-800 flex items-center">
                  <i className="fas fa-envelope mr-2"></i>
                  Original Email Content
                </h3>
              </div>
              
              {/* Scrollable Email Content Container */}
              <div className="flex-1 max-h-96 overflow-y-auto">
                <div className="p-4">
                  {getEmailContent(player) ? (
                    <div 
                      className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: renderEmailContentWithHighlight(player, player.hs_grad_year)
                      }}
                    />
                  ) : (
                    <div className="text-neutral-500 italic py-8 text-center">
                      <i className="fas fa-inbox mb-2 text-2xl block"></i>
                      <p>No email content available for this player.</p>
                      <p className="text-xs mt-2">
                        This player may have been added manually or the email content wasn't preserved.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legend */}
            {player.hs_grad_year && getEmailContent(player) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center text-sm">
                  <i className="fas fa-info-circle text-blue-500 mr-2"></i>
                  <span className="font-medium text-blue-800">Legend:</span>
                  <span className="ml-2 text-blue-700">
                    <mark style={{ backgroundColor: '#fef3c7', padding: '2px 4px', borderRadius: '2px', fontWeight: 'bold', color: '#92400e' }}>
                      Highlighted text
                    </mark>
                    {' '}shows where our model identified the graduation year {player.hs_grad_year}.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex justify-end pt-4 border-t border-neutral-200 flex-shrink-0">
            <Button onClick={onClose} className="bg-primary hover:bg-primary/90">
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Warning
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <i className="fas fa-question-circle text-blue-500 mr-2"></i>
              Confirm Graduation Year Update
            </AlertDialogTitle>
            <AlertDialogDescription>
              {player.hs_grad_year ? (
                <>
                  Are you sure you want to update <strong>{player.name}'s</strong> graduation year from{' '}
                  <strong>{player.hs_grad_year}</strong> to <strong>{pendingGradYear}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to set <strong>{player.name}'s</strong> graduation year to{' '}
                  <strong>{pendingGradYear}</strong>?
                </>
              )}
            </AlertDialogDescription>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-4">
              <div className="flex items-start">
                <i className="fas fa-info-circle text-amber-600 mt-0.5 mr-2"></i>
                <div className="text-sm text-amber-800">
                  {player.hs_grad_year ? (
                    "This is a significant change (more than 2 years). The graduation year will be marked as manually corrected and removed from future warning checks."
                  ) : (
                    "This graduation year is outside the typical range. The graduation year will be marked as manually corrected and removed from future warning checks."
                  )}
                </div>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowConfirmDialog(false);
                setPendingGradYear(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUpdate}
              disabled={updateGradYearMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateGradYearMutation.isPending ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  Yes, Update
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 